import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import './FiltreDeCommande.css';

interface FilterOptions {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  lotTechnique: string;       // sera transformé en operation_lot_technique
  lotGeographique: string;    // sera transformé en operation_lot_departement
  createur: string;           // sera transformé en operation_id_user
  nomCommande: string;        // sera transformé en operation_nom
  client: string;             // sera transformé en operation_organisme_id
  prixRange: {
    min: number;              // deviendra operation_prix_total_min
    max: number;              // deviendra operation_prix_total_max
  };
  status: string;             // sera transformé en status_id
  type: string;               // sera transformé en operation_type_compte
}

interface FiltreDeCommandeProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: any) => void;
  data: any[];
  maxPrice: number;
}

const FiltreDeCommande: React.FC<FiltreDeCommandeProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  data,
  maxPrice
}) => {
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: {
      start: null,
      end: null
    },
    lotTechnique: '',
    lotGeographique: '',
    createur: '',
    nomCommande: '',
    client: '',
    prixRange: {
      min: 0,
      max: maxPrice
    },
    status: '',
    type: ''
  });

  // Mise à jour du prix max lorsqu'il change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      prixRange: { ...prev.prixRange, max: maxPrice }
    }));
  }, [maxPrice]);

  // Extraction des valeurs uniques pour les selects
  const uniqueValues = {
    lotTechnique: [...new Set(data.map(item => item.lotTechnique))],
    lotGeographique: [...new Set(data.map(item => item.lotGeographique))],
    createur: [...new Set(data.map(item => item.creePar))],
    client: [...new Set(data.map(item => item.client))],
    status: [...new Set(data.map(item => item.statut))],
    type: ['Tous', 'BC', 'MS']
  };

  // Transformation des filtres front en clés attendues par le back
  const handleApply = () => {
    const transformedFilters = {
      ...filters, // Conserver les champs originaux, y compris dateRange et prixRange
      operation_id_user: filters.createur,               // Créateur => opération utilisateur
      operation_nom: filters.nomCommande,                // Nom de commande
      operation_lot_technique: filters.lotTechnique,       // Lot technique
      operation_lot_departement: filters.lotGeographique,  // Département (lot géographique)
      operation_prix_total_min: filters.prixRange.min,     // Prix minimum
      operation_prix_total_max: filters.prixRange.max,     // Prix maximum
      status_id: filters.status,                           // Statut
      operation_type_compte: filters.type,                 // Type de compte
      operation_organisme_id: filters.client               // Client => organisme
    };

    onApplyFilters(transformedFilters);
    onClose();
  };

  const handleMinChange = (value: number) => {
    setFilters({
      ...filters,
      prixRange: {
        ...filters.prixRange,
        min: Math.min(value, filters.prixRange.max)
      }
    });
  };

  const handleMaxChange = (value: number) => {
    setFilters({
      ...filters,
      prixRange: {
        ...filters.prixRange,
        max: Math.max(value, filters.prixRange.min)
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="filtre-overlay">
      <div className="filtre-modal">
        <div className="filtre-header">
          <h2>Filtrer les commandes</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="filtre-content">
          <div className="filtre-section">
            <h3>Période</h3>
            <div className="date-range">
              {/* DatePicker peut être activé ici */}
            </div>
          </div>

          <div className="filtre-section">
            <h3>Lots</h3>
            <select
              value={filters.lotTechnique}
              onChange={(e) => setFilters({ ...filters, lotTechnique: e.target.value })}
            >
              <option value="">Tous les lots techniques</option>
              {uniqueValues.lotTechnique.map(lot => (
                <option key={lot} value={lot}>{lot}</option>
              ))}
            </select>

            <select
              value={filters.lotGeographique}
              onChange={(e) => setFilters({ ...filters, lotGeographique: e.target.value })}
            >
              <option value="">Tous les départements</option>
              {uniqueValues.lotGeographique.map(dep => (
                <option key={dep} value={dep}>{dep}</option>
              ))}
            </select>
          </div>

          <div className="filtre-section">
            <h3>Recherche</h3>
            <input
              type="text"
              placeholder="Nom de la commande"
              value={filters.nomCommande}
              onChange={(e) => setFilters({ ...filters, nomCommande: e.target.value })}
            />
          </div>

          <div className="filtre-section">
            <h3>Prix</h3>
            <div className="price-range">
              <div className="price-range-container">
                <div className="inputs-container">
                  <div className="input-group">
                    <label htmlFor="min-price">Min</label>
                    <input
                      id="min-price"
                      type="number"
                      min={0}
                      max={maxPrice}
                      value={filters.prixRange.min}
                      onChange={(e) => handleMinChange(parseInt(e.target.value) || 0)}
                      className="price-input"
                      placeholder="0"
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="max-price">Max</label>
                    <input
                      id="max-price"
                      type="number"
                      min={0}
                      max={maxPrice}
                      value={filters.prixRange.max}
                      onChange={(e) => handleMaxChange(parseInt(e.target.value) || maxPrice)}
                      className="price-input"
                      placeholder={`${maxPrice}`}
                    />
                  </div>
                </div>
                <Button
                  value="Réinitialiser"
                  variant="third"
                  size="small"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      prixRange: { min: 0, max: maxPrice },
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="filtre-section">
            <h3>Autres filtres</h3>
            <select
              value={filters.createur}
              onChange={(e) => setFilters({ ...filters, createur: e.target.value })}
            >
              <option value="">Tous les créateurs</option>
              {uniqueValues.createur.map(createur => (
                <option key={createur} value={createur}>{createur}</option>
              ))}
            </select>

            <select
              value={filters.client}
              onChange={(e) => setFilters({ ...filters, client: e.target.value })}
            >
              <option value="">Tous les clients</option>
              {uniqueValues.client.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">Tous les statuts</option>
              {uniqueValues.status.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              {uniqueValues.type.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filtre-actions">
          <Button
            value="Annuler"
            variant="secondary"
            size="medium"
            onClick={onClose}
          />
          <Button
            value="Appliquer"
            variant="primary"
            size="medium"
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};

export default FiltreDeCommande;